/**
 * Inbox Card
 *
 * Posteingang im Chat, live von LinkedIn: entweder die Gesprächsliste eines Kontos
 * (inbox_list) oder der Verlauf eines Gesprächs (inbox_messages). Nur Anzeige — Antworten
 * laufen über den Chat und dessen Sendesperre.
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, Chip, Stack, IconButton, Tooltip, Button } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

export interface InboxCardThread {
  participantName?: string;
  participantHeadline?: string;
  participantProfileUrl?: string;
  lastMessage?: string;
  lastMessageFromMe?: boolean;
  lastMessageAt?: string;
  unreadCount?: number;
}

export interface InboxCardMessage {
  senderName?: string;
  content: string;
  sentAt?: string;
  isFromMe?: boolean;
}

export type InboxCardData =
  | { mode: 'threads'; threads: InboxCardThread[]; total?: number; unreadTotal?: number; unreadOnly?: boolean }
  | { mode: 'messages'; participantName?: string; messages: InboxCardMessage[]; hasMore?: boolean };

function fmt(date?: string): string {
  if (!date) return '';
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? String(date)
    : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const PREVIEW = 400;

const InboxCard: React.FC<{ data: InboxCardData }> = ({ data }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState<Record<number, boolean>>({});

  if (data.mode === 'messages') {
    const messages = data.messages || [];
    return (
      <Paper className={classes.card} elevation={0}>
        <Stack direction="row" alignItems="center" gap={1}>
          <ForumIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" className={classes.title}>
            {data.participantName
              ? t('inboxConversationWith', 'Gespräch mit {{name}}', { name: data.participantName })
              : t('inboxConversation', 'Gespräch')}
          </Typography>
        </Stack>
        {data.hasMore && (
          <Typography variant="caption" color="textSecondary">
            {t('inboxOlderInApp', 'Ältere Nachrichten stehen im Posteingang der App.')}
          </Typography>
        )}
        {messages.map((m, i) => {
          const long = m.content.length > PREVIEW;
          return (
            <Box key={i} className={cx(classes.message, !m.isFromMe && classes.received)}>
              <Typography variant="caption" color="textSecondary" component="div">
                {m.isFromMe ? t('contactYou', 'Du') : m.senderName || data.participantName || t('contactThem', 'Kontakt')}
                {m.sentAt ? ` · ${fmt(m.sentAt)}` : ''}
              </Typography>
              <Typography variant="body2" className={classes.prewrap}>
                {open[i] || !long ? m.content || '—' : `${m.content.slice(0, PREVIEW).trimEnd()} …`}
              </Typography>
              {long && (
                <Button size="small" className={classes.more} onClick={() => setOpen((s) => ({ ...s, [i]: !s[i] }))}>
                  {open[i] ? t('showLess', 'weniger') : t('showMore', 'mehr')}
                </Button>
              )}
            </Box>
          );
        })}
        {messages.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            {t('inboxNoMessages', 'Keine Nachrichten in diesem Gespräch.')}
          </Typography>
        )}
      </Paper>
    );
  }

  const threads = data.threads || [];
  return (
    <Paper className={classes.card} elevation={0}>
      <Stack direction="row" alignItems="center" gap={1}>
        <ForumIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" className={classes.title} sx={{ flexGrow: 1 }}>
          {data.unreadOnly ? t('inboxUnreadTitle', 'Ungelesene Gespräche') : t('inboxTitle', 'Posteingang')}
        </Typography>
        {!!data.unreadTotal && <Chip size="small" color="primary" label={t('inboxUnreadCount', '{{count}} ungelesen', { count: data.unreadTotal })} />}
      </Stack>
      {threads.map((thread, i) => (
        <Stack key={i} direction="row" gap={1} alignItems="flex-start" className={classes.thread}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" gap={1} alignItems="baseline">
              <Typography variant="body2" noWrap className={cx(classes.name, !!thread.unreadCount && classes.unread)}>
                {thread.participantName || t('unknownContact', 'Unbekannter Kontakt')}
              </Typography>
              <Typography variant="caption" color="textSecondary" noWrap sx={{ flexShrink: 0 }}>
                {fmt(thread.lastMessageAt)}
              </Typography>
            </Stack>
            {thread.participantHeadline && (
              <Typography variant="caption" color="textSecondary" noWrap component="div">
                {thread.participantHeadline}
              </Typography>
            )}
            <Typography variant="caption" component="div" className={classes.preview}>
              {thread.lastMessageFromMe ? `${t('contactYou', 'Du')}: ` : ''}
              {thread.lastMessage || '—'}
            </Typography>
          </Box>
          {!!thread.unreadCount && <Chip size="small" color="primary" label={thread.unreadCount} />}
          {thread.participantProfileUrl && (
            <Tooltip title={t('viewOnLinkedIn', 'Auf LinkedIn ansehen')}>
              <IconButton size="small" onClick={() => window.open(thread.participantProfileUrl, '_blank', 'noopener')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ))}
      {threads.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {data.unreadOnly ? t('inboxNoUnread', 'Keine ungelesenen Gespräche.') : t('inboxEmpty', 'Keine Gespräche gefunden.')}
        </Typography>
      )}
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 560,
  },
  title: {
    fontWeight: 600,
  },
  thread: {
    paddingTop: theme.spacing(0.75),
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
  name: {
    minWidth: 0,
    flexGrow: 1,
  },
  unread: {
    fontWeight: 600,
  },
  preview: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    color: theme.palette.text.secondary,
  },
  prewrap: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  message: {
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    alignSelf: 'flex-start',
    maxWidth: '92%',
  },
  received: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light + '33',
  },
  more: {
    alignSelf: 'flex-start',
    padding: 0,
    minWidth: 0,
    textTransform: 'none',
  },
}));

export default InboxCard;
